import * as React from 'react';
import { Forward, Send, Search, X } from 'lucide-react';
import { SlideOver, Button, useToast } from '@/components/ui';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { api } from '@/lib/api-client';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { formatDateTime } from '@/lib/date-format';
import { useI18n } from '@/lib/i18n';

interface ForwardableConversation {
  id: string;
  type?: string;
  body: string;
  createdAt: string;
  user: { firstName: string; lastName: string } | null;
}

interface ContactResult {
  value: string;
  label: string;
  sublabel?: string;
}

interface ForwardDrawerProps {
  ticketId: string;
  conversation: ForwardableConversation | null;
  conversations: ForwardableConversation[];
  onClose: () => void;
  onForwarded?: () => void;
  timezone?: string;
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * Inline contact-or-email picker for the forward drawer. A single input
 * that searches contacts on the server and also accepts a free-typed email.
 * Selecting a contact fills the input with their email address directly —
 * no separate "selected" vs "searching" visual states that can desync.
 *
 * ponytail: replaces SearchableSelect here because the two-state
 * (button vs input) pattern in SearchableSelect creates a value/query
 * feedback loop when free-text is enabled AND the parent re-feeds the
 * typed value back as `value` on every keystroke.
 */
function RecipientInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (email: string) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const [query, setQuery] = React.useState(value);
  const [results, setResults] = React.useState<ContactResult[]>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const searchTimer = React.useRef<ReturnType<typeof setTimeout>>();

  // Sync parent value into local query when it changes externally (e.g. reset)
  React.useEffect(() => { setQuery(value); }, [value]);

  React.useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setQuery(next);
    onChange(next);
    setOpen(true);
    clearTimeout(searchTimer.current);
    if (next.trim().length < 2) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get<{ data: ContactResult[] }>('/contacts/search', { params: { q: next.trim() } });
        setResults(res.data);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 300);
  }

  function selectContact(contact: ContactResult) {
    const email = contact.sublabel || contact.label;
    setQuery(email);
    onChange(email);
    setResults([]);
    setOpen(false);
  }

  function handleClear() {
    setQuery('');
    onChange('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <label className="mb-1 block text-sm font-medium text-ink-muted">{t('tp.forward.to')}</label>
      <div className="flex h-10 items-center rounded-md border border-line-strong bg-white px-3 text-sm">
        <Search className="mr-2 h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => !disabled && query.trim().length >= 2 && setOpen(true)}
          onBlur={(e) => {
            const next = e.relatedTarget as Node | null;
            if (!next || !containerRef.current?.contains(next)) setOpen(false);
          }}
          placeholder={t('tp.forward.searchContact')}
          disabled={disabled}
          className="flex-1 bg-transparent outline-none placeholder:text-ink-subtle"
          aria-label={t('tp.forward.recipientEmail')}
        />
        {query && (
          <button type="button" onClick={handleClear} className="ml-1 shrink-0 text-ink-subtle hover:text-ink-muted" aria-label={t('tp.common.clear')}>
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-white shadow-raised">
          {loading && <div className="px-3 py-2 text-sm text-ink-subtle">{t('tp.common.loading')}</div>}
          {!loading && results.length === 0 && query.trim().length >= 2 && (
            <div className="px-3 py-2 text-sm text-ink-subtle">
              {isValidEmail(query.trim()) ? t('tp.forward.useThisEmail') : t('tp.forward.noContacts')}
            </div>
          )}
          {!loading && results.map((contact) => (
            <button
              key={contact.value}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); selectContact(contact); }}
              className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-surface-hover"
            >
              <span className="font-medium">{contact.label}</span>
              {contact.sublabel && <span className="text-xs text-ink-subtle">{contact.sublabel}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Forward side drawer — opened from a conversation card's Forward icon
 * (admin/super-admin by default, configurable via role permissions). The
 * selected conversation and all earlier visible conversations are quoted into
 * the body; later conversations are never included.
 */
export function ForwardDrawer({ ticketId, conversation, conversations, onClose, onForwarded, timezone }: ForwardDrawerProps) {
  const { toast } = useToast();
  const { t } = useI18n();
  const [to, setTo] = React.useState('');
  const [extraBody, setExtraBody] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const sendingRef = React.useRef(false);
  const editorKey = React.useRef(0);

  const forwardedConversations = React.useMemo(() => {
    if (!conversation) return [];
    const ordered = conversations.filter((entry) => entry.type !== 'system');
    const selectedIndex = ordered.findIndex((entry) => entry.id === conversation.id);
    return selectedIndex >= 0 ? ordered.slice(0, selectedIndex + 1) : [conversation];
  }, [conversation, conversations]);

  React.useEffect(() => {
    if (conversation) {
      setTo('');
      setExtraBody('');
      setError(null);
      editorKey.current += 1;
    }
  }, [conversation]);

  async function sendForward() {
    if (sendingRef.current) return;
    const recipient = to.trim();
    if (!conversation || !recipient) {
      setError(t('tp.forward.recipientRequired'));
      return;
    }
    if (!isValidEmail(recipient)) {
      setError(t('tp.forward.recipientInvalid'));
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      await api.post(`/tickets/${ticketId}/conversations`, {
        type: 'forward',
        body: extraBody || '<p></p>',
        toEmails: [recipient],
        quoteConversationIds: forwardedConversations.map((c) => c.id),
      });

      toast({ variant: 'success', title: t('tp.forward.forwarded') });
      onForwarded?.();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('tp.forward.failed');
      setError(message);
      toast({ variant: 'error', title: t('tp.forward.failed'), description: message });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  return (
    <SlideOver
      open={!!conversation}
      onClose={onClose}
      widthClassName="max-w-lg"
      title={(
        <span className="flex items-center gap-2">
          <Forward className="h-4 w-4 text-brand-600" aria-hidden="true" />
          {t('tp.forward.title')}
        </span>
      )}
      footer={conversation ? (
        <div className="flex items-center justify-between gap-2">
          {error && <span className="text-xs text-danger-600" role="alert">{error}</span>}
          <div className="flex-1" />
          <Button variant="ghost" size="sm" disabled={sending} onClick={onClose}>{t('tp.common.cancel')}</Button>
          <Button size="sm" disabled={sending || !to.trim()} onClick={() => void sendForward()}>
            <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {sending ? t('tp.forward.forwarding') : t('tp.forward.title')}
          </Button>
        </div>
      ) : undefined}
    >
      {conversation && (
        <div className="space-y-4 p-5">
          <RecipientInput
            key={editorKey.current}
            value={to}
            onChange={setTo}
            disabled={sending}
          />
          <RichTextEditor
            key={`editor-${editorKey.current}`}
            value=""
            onChange={setExtraBody}
            label={t('tp.forward.message')}
            placeholder={t('tp.forward.messagePlaceholder')}
            minHeight="90px"
            disabled={sending}
          />
          <div className="rounded-md border border-line bg-surface-hover p-3">
            <p className="mb-2 text-xs font-medium text-ink-subtle">
              {forwardedConversations.length === 1 ? t('tp.forward.forwardingOne') : t('tp.forward.forwardingMany', { count: forwardedConversations.length })}
            </p>
            <div className="space-y-3">
              {forwardedConversations.map((entry) => (
                <div key={entry.id} className="border-t border-line pt-2 first:border-t-0 first:pt-0">
                  <p className="mb-1 text-xs text-ink-subtle">
                    {entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : t('tp.common.system')} · {formatDateTime(entry.createdAt, timezone)}
                  </p>
                  <div
                    className="ticket-rich-content prose prose-sm max-w-none text-sm"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(entry.body) }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </SlideOver>
  );
}
